import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  TextField,
  Button,
  Drawer,
  Box,
  Tabs,
  Tab,
  MenuItem,
  IconButton
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import "../App.css";
import MeetingList from "./MeetingList"; // Import the MeetingList component

const Header = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [startDate, setStartDate] = useState("2026-01-01");
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]); // Today's date
  const [organization, setOrganization] = useState("");
  const [type, setType] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [meetings, setMeetings] = useState([]);
  const [error, setError] = useState(null);
  const [filterChanged, setFilterChanged] = useState(false); // Tracks if filters are modified

  const today = new Date().toISOString().split('T')[0]; // Get today's date
const minStartDate = "2008-01-01"; // Minimum start date

const handleDateChange = (setter) => (event) => {
  let selectedDate = event.target.value;

  if (setter === setStartDate) {
    selectedDate = selectedDate < minStartDate ? minStartDate : selectedDate > today ? today : selectedDate;
  } else if (setter === setEndDate) {
    selectedDate = selectedDate < minStartDate ? minStartDate : selectedDate > today ? today : selectedDate;
  }

  setter(selectedDate);
  setFilterChanged(true);
};

  const toggleDrawer = (open) => () => {
    if (!open && filterChanged) {
      fetchMeetings(); // Fetch data only when closing drawer after changes
      setFilterChanged(false); // Reset flag after fetching
    }
    setDrawerOpen(open);
  };

  const handleYearChange = (event, newYear) => {
    setSelectedYear(newYear.toString());
    setStartDate(`${newYear.toString()}-01-01`);
    setEndDate(`${newYear.toString()}-12-31`);
  };

  const handleSearchEnter = (e) => {
    if (e.key === "Enter" && searchKeyword.length >= 3) {
      fetchMeetings();
    }
  };

  const handleFilterChange = (setter) => (event) => {
    setter(event.target.value);
    setFilterChanged(true);
  };
  const applyFilters = () => {
    if (filterChanged) {
      console.log("Applying filters with:", { startDate, endDate, organization, type });
      fetchMeetings();
      setFilterChanged(false); // Reset after applying
    }
  };
  const fetchMeetings = async () => {
    try {
      setLoading(true);
      console.log("Fetching meetings for:", selectedYear, searchKeyword, startDate, endDate, organization, type);

      let response;
      if (searchKeyword || filterChanged) {
        response = await axios.post("https://sire.bostonplans.org/api/search/memos", {
          query: searchKeyword || "",
          exact: false,
          startDate,
          endDate,
          type,
          organization,
        });
      } else {
        response = await axios.get(`https://sire.bostonplans.org/api/search/${selectedYear}`);
      }

      const data = response.data.results || response.data;
      const groupedMeetings = data.reduce((acc, item) => {
        const bpdaBoard = item.metadata?.enterprise?.bpdaBoard;
        if (!bpdaBoard || !bpdaBoard.meetingDate) {
          console.warn("Invalid item structure:", item);
          return acc;
        }

        let date = new Date(bpdaBoard.meetingDate);
        date.setDate(date.getDate() + 1);
        date = date
          .toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
          .replace(",", "");

        if (!acc[date]) {
          acc[date] = { title: date, items: [], count: 0 };
        }

        acc[date].items.push({
          id:item.id,
          name: item.name,
          metadataDetails: bpdaBoard,
          organization: bpdaBoard.organization,
          url: item.shared_link?.url || "No Shared Link Available",
        });

        acc[date].count += 1;

        return acc;
      }, {});

      const sortedMeetings = Object.keys(groupedMeetings)
        .sort((a, b) => new Date(b) - new Date(a))
        .map((key) => groupedMeetings[key]);

      setMeetings(sortedMeetings);
    } catch (err) {
      setError("Failed to fetch meeting data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetings();
  }, [selectedYear]);

  const organizations = ["All", "BRA", "EDIC"];
  const types = ["All", "Agenda", "Approved Agenda", "Minutes", "Memo", "Informational Update", "Presentation", "Support Material"];

  return (
    <div>
      <AppBar position="static" style={{ marginBottom: "20px", backgroundColor: "#091F2F" }}>
        <Toolbar sx={{ gap: 1, display: "flex", alignItems: "center" }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Search Meetings
          </Typography>
          <TextField
            variant="outlined"
            placeholder="Search..."
            size="small"
            value={searchKeyword}
            onChange={handleFilterChange(setSearchKeyword)}
            onKeyDown={handleSearchEnter}
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: "4px",
              height: "40px",
              width: "200px",
              "& .MuiOutlinedInput-root": {
                height: "100%",
                color: "#007BFF",
                "& fieldset": {
                  borderColor: "#007BFF",
                },
                "&:hover fieldset": {
                  borderColor: "#0056b3",
                },
              },
            }}
          />
          <IconButton color="primary" onClick={handleSearchEnter} sx={{ height: "40px" }}>
            <SearchIcon />
          </IconButton>
          <Button variant="contained" color="primary" onClick={toggleDrawer(true)} sx={{ height: "40px", minWidth: "150px" }}>
            Advanced Search
          </Button>
        </Toolbar>

        <Tabs value={Number(selectedYear)} onChange={handleYearChange} variant="scrollable" scrollButtons="auto" textColor="inherit" indicatorColor="secondary" sx={{ backgroundColor: "#f5f5f5", color: "#091F2F", height: "48px" }}>
          {Array.from({ length: 2026 - 2008 + 1 }, (_, i) => 2026 - i).map((year) => (
            <Tab key={year} label={year} value={year} sx={{ height: "48px" }} />
          ))}
        </Tabs>
      </AppBar>

      <Box className="meeting-list-container">
        <MeetingList year={selectedYear} searchKeyword={searchKeyword} startDate={startDate} endDate={endDate} error={error} meetings={meetings} loading={loading} />
      </Box>

      <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box sx={{ width: 300, padding: "20px" }} role="presentation">
          <Typography variant="h6" gutterBottom>
            Advanced Search
          </Typography>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={handleDateChange(setStartDate)}
            fullWidth
            sx={{ marginBottom: "20px" }}
            inputProps={{ min: minStartDate, max: today }} 
          />

          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={handleDateChange(setEndDate)}
            fullWidth
            sx={{ marginBottom: "20px" }}
            inputProps={{ min: minStartDate, max: today }} 
          />
        <TextField select label="Select Organization" value={organization} onChange={handleFilterChange(setOrganization)} fullWidth sx={{ marginBottom: "20px" }}>
            {organizations.map((org, index) => (
              <MenuItem key={index} value={org}>
                {org}
              </MenuItem>
            ))}
        </TextField>
        <TextField select label="Select Type" value={type} onChange={handleFilterChange(setType)} fullWidth sx={{ marginBottom: "20px" }}>
            {types.map((type, index) => (
              <MenuItem key={index} value={type}>
                {type}
              </MenuItem>
            ))}
        </TextField>
          <Button
            variant="contained"
            color="primary"
            sx={{ height: '40px', width: '100%', marginTop: '20px' }}
            onClick={() => {
              applyFilters(); // Call filter function
              setDrawerOpen(false);  // Close the drawer after applying filter
            }}
          >
            Apply Filter
          </Button>

        </Box>
      </Drawer>
    </div>
  );
};

export default Header;
